import gql from 'graphql-tag';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import type { PetitionUpdateType } from '@change-corgi/middle-end/gql';

import type {
	GammaPetitionDetailsPagePetitionUpdatesQuery,
	GammaPetitionDetailsPagePetitionUpdatesQueryVariables,
} from './getPetitionUpdates.graphql';
import { normalizePetitionInfo } from './normalizePetitionInfo';
import { normalizeUpdates } from './normalizeUpdates';
import type { PetitionUpdate, PetitionUpdates } from './types';

// TODO(CHANGE-68070): While fixing the loading state of the update component, we can also consolidate the GQL fragments
// into the pageData definitions. See: petitionGamma/details/pageContext/pageData/petitionUpdates.graphql
const QUERY = gql`
	query GammaPetitionDetailsPagePetitionUpdates(
		$petitionId: ID!
		$count: Int!
		$types: [PetitionUpdateType!]!
		$cursor: ID
	) {
		petition: petitionById(id: $petitionId) {
			id
			slug

			user {
				id
				displayName
				photo {
					id
					userSmall {
						url
						processing
					}
				}
			}
			organization {
				id
				name
				photo {
					id
					userSmall {
						url
						processing
					}
				}
			}

			updatesConnection: petitionUpdatesConnection(types: $types, first: $count, after: $cursor) {
				nodes {
					__typename
					id
					createdAt
					createdAtLocalized(format: medium)
					... on MilestonePetitionUpdate {
						signatureCount
					}
					... on StarterPetitionUpdate {
						title
						description
						...GammaPetitionDetailsPagePetitionUpdatesMediaFields
					}
					... on StaffPetitionUpdate {
						title
						description
						...GammaPetitionDetailsPagePetitionUpdatesMediaFields
					}
					... on FeaturedNewsPetitionUpdate {
						title
						...GammaPetitionDetailsPagePetitionUpdatesMediaFields
					}
					... on DmResponsePetitionUpdate {
						title
						description
						...GammaPetitionDetailsPagePetitionUpdatesMediaFields
						dmResponse {
							id
							decisionMaker {
								id
								displayName
								photo {
									id
									userSmall {
										url
										processing
									}
								}
							}
						}
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	}

	fragment GammaPetitionDetailsPagePetitionUpdatesMediaFields on PetitionUpdateWithMedia {
		photo {
			id
			petitionMedium {
				url
				processing
			}
		}
		embeddedMedia {
			providerName
			photoUrl
			description
		}
	}
`;

type Options = Readonly<{
	count: number;
	cursor?: string;
	types?: readonly PetitionUpdateType[];
}>;

export async function getPetitionUpdates<U extends PetitionUpdate = PetitionUpdate>(
	petitionId: string,
	{ cursor, count, types }: Options,
	{ gql: { fetch } }: UtilityContext,
): Promise<PetitionUpdates<U> | undefined> {
	const { data } = await fetch<
		GammaPetitionDetailsPagePetitionUpdatesQuery,
		GammaPetitionDetailsPagePetitionUpdatesQueryVariables
	>({
		query: QUERY,
		variables: {
			petitionId,
			count,
			types: types || ['MILESTONE', 'STARTER', 'DM_RESPONSE', 'STAFF', 'FEATURED_NEWS'],
			cursor: cursor || null,
		},
		// to separate query from other batched queries
		important: true,
	});
	const petition = data?.petition;
	if (!petition) {
		return undefined;
	}
	const { slug, organization, user } = petition;
	return normalizeUpdates<U>(petition.updatesConnection, normalizePetitionInfo({ slug, organization, user }));
}
